import React, { useState, useRef, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import './UpperBar.css';
import frenchFlag from '../../../images/fr.png';
import englishFlag from '../../../images/en.png';
import arabicFlag from '../../../images/ar.png';
import { setSelectedRole } from '../../../redux/actions/roleActions';
import { setAuthenticatedUser } from '../../../redux/actions/authActions';
import { logout } from '../../../redux/actions/authActions';
import { useTranslation } from "react-i18next";
import { useNavigate } from 'react-router-dom';
import {API_BASE_URL} from "../../../../api";
import { 
    Bell, 
    Check, 
    CheckCheck, 
    User, 
    LogOut,
    Inbox,
    Grid,
    ChevronDown,
    Settings,
    CheckSquare,
    Menu
} from 'lucide-react';
import { 
    getRecentNotificationsRequest, 
    getUnreadCountRequest,
    markNotificationAsReadRequest, 
    markAllNotificationsAsReadRequest 
} from '../../../redux/actions/notificationActions';
import { getMyTasksRequest } from '../../../redux/actions/taskActions';

const getInitials = (user) => {
    const first = (user?.firstName || '').trim();
    const last = (user?.lastName || '').trim();
    if (first || last) return `${first.charAt(0)}${last.charAt(0)}`.toUpperCase() || 'U';

    const parts = (user?.fullName || '').trim().split(/\s+/).filter(Boolean);
    return `${parts[0]?.charAt(0) || ''}${parts[1]?.charAt(0) || ''}`.toUpperCase() || 'U';
};

const UpperBar = ({ showModules = true }) => {
    const { t, i18n } = useTranslation();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const translateRole = (role) => {
        const name = String(role?.name || role || "").trim();
        const roleKeys = {
            SUPER_ADMIN: "Super administrateur", ADMIN: "Administrateur", FORMATION_RESP: "Responsable formation",
            SKILLS_MANAGER: "Responsable compétences", HR_MANAGER: "Administrateur RH", EMPLOYEE: "Collaborateur",
            "Administrateur RH": "Administrateur RH",
        };
        return t(roleKeys[name] || name, { defaultValue: name });
    };

    const [isModulesOpen, setIsModulesOpen] = useState(false);
    const [isNotificationsOpen, setIsNotificationsOpen] = useState(false);
    const [isUserDropdownOpen, setIsUserDropdownOpen] = useState(false);
    const [isModuleSwitcherOpen, setIsModuleSwitcherOpen] = useState(false);
    const [isTasksOpen, setIsTasksOpen] = useState(false);

    const modulesRef = useRef(null);
    const notificationsRef = useRef(null);
    const tasksRef = useRef(null);
    const userDropdownRef = useRef(null);
    const moduleSwitcherRef = useRef(null);

    const selectedRole = useSelector(state => state.role?.selectedRole);
    const currentUser = useSelector(state => state.auth?.user);

    // Les rôles peuvent être modifiés par un administrateur pendant que
    // l'utilisateur est déjà connecté. On synchronise alors uniquement son
    // profil, discrètement, sans déclencher de loader global.
    useEffect(() => {
        if (!currentUser?.id) return undefined;

        let cancelled = false;
        const syncCurrentUser = async () => {
            if (document.visibilityState !== 'visible') return;

            const token = sessionStorage.getItem('token') || sessionStorage.getItem('accessToken');
            const uid = sessionStorage.getItem('uid');
            try {
                const response = await fetch(`${API_BASE_URL}/api/users/${currentUser.id}`, {
                    headers: {
                        Authorization: token ? `Bearer ${token}` : '',
                        uid: uid || '',
                    },
                });
                if (!response.ok || cancelled) return;

                const updatedUser = await response.json();
                const currentRoles = (currentUser.roles || []).map(role => role.id).sort().join(',');
                const updatedRoles = (updatedUser.roles || []).map(role => role.id).sort().join(',');

                if (currentRoles !== updatedRoles) {
                    dispatch(setAuthenticatedUser({ ...currentUser, ...updatedUser }));
                }
            } catch (error) {
                // Une erreur ponctuelle de synchronisation ne doit pas perturber l'interface.
                console.warn('Impossible de synchroniser les rôles de l’utilisateur connecté.', error);
            }
        };

        syncCurrentUser();
        const intervalId = window.setInterval(syncCurrentUser, 30_000);
        const syncOnFocus = () => syncCurrentUser();
        window.addEventListener('focus', syncOnFocus);
        document.addEventListener('visibilitychange', syncOnFocus);

        return () => {
            cancelled = true;
            window.clearInterval(intervalId);
            window.removeEventListener('focus', syncOnFocus);
            document.removeEventListener('visibilitychange', syncOnFocus);
        };
    }, [currentUser, dispatch]);

    // Notifications Redux
    const { unreadNotifications, unreadCount } = useSelector(state => state.notification || { unreadNotifications: [], unreadCount: 0 });
    const { tasks = [] } = useSelector(state => state.tasks || { tasks: [] });

    useEffect(() => {
        if (currentUser?.id && selectedRole?.id) {
            dispatch(getRecentNotificationsRequest(currentUser.id, selectedRole.id));
            dispatch(getUnreadCountRequest(currentUser.id, selectedRole.id));
            dispatch(getMyTasksRequest(currentUser.id, selectedRole.id));
            const timer = setInterval(() => {
                dispatch(getRecentNotificationsRequest(currentUser.id, selectedRole.id));
                dispatch(getUnreadCountRequest(currentUser.id, selectedRole.id));
                dispatch(getMyTasksRequest(currentUser.id, selectedRole.id));
            }, 15000); // Poll every 15 seconds for more responsiveness
            return () => clearInterval(timer);
        }
    }, [currentUser?.id, selectedRole?.id, dispatch]);

    const hasProfilePhoto = !!(currentUser?.profilePictureUrl && String(currentUser.profilePictureUrl).trim());
    const profilePhotoSrc = hasProfilePhoto
        ? `${API_BASE_URL}/api/users/images/profile/${currentUser.profilePictureUrl}`
        : null;
    const userInitials = getInitials(currentUser);

    const allRoles = currentUser?.roles || [];

    useEffect(() => {
        if (allRoles.length > 0 && !selectedRole) {
             const currentPath = window.location.pathname;
             let autoSelectedRole = allRoles[0];
             
             if (currentPath.includes('/skills')) {
                 autoSelectedRole = allRoles.find(r => r.code?.trim() === 'SKILLS') || autoSelectedRole;
             } else if (currentPath.includes('/formation')) {
                 autoSelectedRole = allRoles.find(r => r.code?.trim() === 'FORMATION') || autoSelectedRole;
             } else if (currentPath.includes('/performance')) {
                 autoSelectedRole = allRoles.find(r => r.code?.trim() === 'PERF') || autoSelectedRole;
             } else if (currentPath.includes('/recrutement')) {
                 autoSelectedRole = allRoles.find(r => r.code?.trim() === 'RECRUTEMENT') || autoSelectedRole;
             } else if (currentPath.includes('/handbook')) {
                 autoSelectedRole = allRoles.find(r => r.code?.trim() === 'HANDBOOK') || autoSelectedRole;
             }

             dispatch(setSelectedRole(autoSelectedRole));
        }
    }, [allRoles, selectedRole, dispatch]);

    const userRoles = allRoles.filter(
        role => role.moduleId === selectedRole?.moduleId
    );

    useEffect(() => {
        const isRTL = i18n.language === 'ar';
        document.documentElement.setAttribute('dir', isRTL ? 'rtl' : 'ltr');
        document.documentElement.setAttribute('lang', i18n.language);
    }, [i18n.language]);

    /* ── Module Switcher data ── */
    const MODULE_ICONS = {
        FORMATION:   { emoji: '📚', bg: '#DBEAFE' },
        SKILLS:      { emoji: '🎯', bg: '#EDE9FE' },
        PERF:        { emoji: '📊', bg: '#D1FAE5' },
        RECRUTEMENT: { emoji: '👥', bg: '#FEF3C7' },
        HANDBOOK:    { emoji: '📖', bg: '#FEE2E2' },
        SELFCARE:    { emoji: '💙', bg: '#E0F2FE' },
    };

    const MODULE_ROUTES = {
        SELFCARE:    '/collaborateur/dashboard',
        SKILLS:      '/skills/dashboard/collaborateur',
        FORMATION:   '/formation/dashboard/collaborateur',
        PERF:        '/performance/dashboard/collaborateur',
        RECRUTEMENT: '/recrutement/dashboard/collaborateur',
        HANDBOOK:    '/handbook/dashboard/collaborateur',
    };

    const getModuleCode = (moduleName) => {
        const name = String(moduleName || '')
            .normalize('NFD')
            .replace(/[\u0300-\u036f]/g, '')
            .trim()
            .toUpperCase();

        if (name.includes('FORMATION')) return 'FORMATION';
        if (name.includes('COMPETENCE') || name.includes('SKILL')) return 'SKILLS';
        if (name.includes('PERFORMANCE')) return 'PERF';
        if (name.includes('RECRUTEMENT')) return 'RECRUTEMENT';
        if (name.includes('HANDBOOK')) return 'HANDBOOK';
        if (name.includes('SELFCARE') || name.includes('CORE RH')) return 'SELFCARE';
        return '';
    };

    const userModulesForSwitcher = (() => {
        if (!currentUser?.roles) return [];
        const map = new Map();
        currentUser.roles.forEach(role => {
            if (role.moduleId && role.moduleName) {
                const existing = map.get(role.moduleId);
                const isPreferredRole = role.isDefault === true && existing?.isDefault !== true;
                if (!existing || isPreferredRole) {
                    map.set(role.moduleId, {
                        id: role.moduleId,
                        name: role.moduleName,
                        // role.code identifies a role, not a module.
                        code: getModuleCode(role.moduleName),
                        roleName: role.name,
                        isDefault: role.isDefault === true,
                    });
                }
            }
        });
        return Array.from(map.values());
    })();

    const handleModuleSwitcherClick = (module) => {
        const moduleRoles = currentUser?.roles?.filter(r => r.moduleId === module.id && r.isActive !== false) || [];
        const defaultRole = moduleRoles.find(r => r.isDefault === true) || moduleRoles[0];
        if (defaultRole) {
            sessionStorage.setItem("roleChanged", "true");
            dispatch(setSelectedRole(defaultRole));
        }
        const route = MODULE_ROUTES[module.code] || '/collaborateur/dashboard';
        navigate(route);
        setIsModuleSwitcherOpen(false);
        setIsModulesOpen(false); // ← ajoute cette ligne
    };

    const currentPath = window.location.pathname;
    const activeModuleCode = Object.entries(MODULE_ROUTES)
        .find(([, path]) => currentPath.startsWith(path))?.[0];

    /* ── Notifications ── */
    // Static list removed, using Redux state

    /* ── Tasks ── */

    const modules = [
        { name: 'Core RH',           description: 'Centralisez facilement vos données RH',     icon: '📁', color: '#60A5FA', bgColor: '#DBEAFE' },
        { name: 'Congés et absences', description: 'Gérez mieux vos demandes de congés',        icon: '🏝️', color: '#F87171', bgColor: '#FEE2E2' },
        { name: 'Temps et activités', description: 'Déclarez facilement vos timesheets',         icon: '🕐', color: '#A78BFA', bgColor: '#EDE9FE' },
        { name: 'Notes de frais',     description: 'Simplifiez la gestion de vos dépenses',     icon: '🧾', color: '#34D399', bgColor: '#D1FAE5' },
        { name: 'Télétravail',        description: 'Maîtrisez votre charte de télétravail',     icon: '💻', color: '#60A5FA', bgColor: '#DBEAFE' },
        { name: 'Bulletins de paie',  description: 'Dématérialisez vos bulletins de paie',      icon: '📄', color: '#FBBF24', bgColor: '#FEF3C7' },
    ];

    const roleIcons = {
        'SUPER_ADMIN': '👑', 'ADMIN': '👔', 'FORMATION_RESP': '📚',
        'SKILLS_MANAGER': '🎯', 'HR_MANAGER': '👨‍💼', 'EMPLOYEE': '👤',
        'Administrateur RH': '👨‍💼', 'Collaborateur': '👤', 'Manager': '👔',
        'Admin': '⚙️', 'Meta admin': '👑', 'DEFAULT': '👤',
    };
    const getRoleIcon = (roleName) => roleIcons[roleName] || roleIcons['DEFAULT'];

    /* ── Click outside ── */
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (modulesRef.current && !modulesRef.current.contains(event.target))
                setIsModulesOpen(false);
            if (notificationsRef.current && !notificationsRef.current.contains(event.target))
                setIsNotificationsOpen(false);
            if (tasksRef.current && !tasksRef.current.contains(event.target))
                setIsTasksOpen(false);
            if (userDropdownRef.current && !userDropdownRef.current.contains(event.target))
                setIsUserDropdownOpen(false);
            if (moduleSwitcherRef.current && !moduleSwitcherRef.current.contains(event.target))
                setIsModuleSwitcherOpen(false);
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Redux actions take care of this
    const unreadCountBadge = unreadCount > 9 ? '9+' : unreadCount;

    const handleRoleChange = (role) => {
        sessionStorage.setItem("roleChanged", "true");
        dispatch(setSelectedRole(role));
        setIsUserDropdownOpen(false);
    };
    const handleModuleClick = (moduleName) => {
        setIsModulesOpen(false);
    };
    const changeLanguage = (lng) => i18n.changeLanguage(lng);
    const handleLogout = () => {
        dispatch(logout());
        setIsUserDropdownOpen(false);
        navigate('/');
    };
    const handleProfileClick = () => {
        setIsUserDropdownOpen(false);
        navigate('/cards');
    };

    return (
        <div className="upper-bar">
            <div className="upper-bar-content">
                <button
                    type="button"
                    className="mobile-navigation-toggle"
                    onClick={() => window.dispatchEvent(new Event('toggle-mobile-sidebar'))}
                    aria-label={t('Ouvrir le menu de navigation')}
                >
                    <Menu size={23} aria-hidden="true" />
                </button>
                <div className="right-section">


                    {/* ── Language Flags ── */}
                    <div className="language-flags">
                        <button onClick={() => changeLanguage('fr')}
                                className={`flag-button ${i18n.language === 'fr' ? 'active' : ''}`} title="Français">
                            <img src={frenchFlag} alt="Français" className="flag-image"/>
                        </button>
                        <button onClick={() => changeLanguage('en')}
                                className={`flag-button ${i18n.language === 'en' ? 'active' : ''}`} title="English">
                            <img src={englishFlag} alt="English" className="flag-image"/>
                        </button>
                        <button onClick={() => changeLanguage('ar')}
                                className={`flag-button ${i18n.language === 'ar' ? 'active' : ''}`} title="العربية">
                            <img src={arabicFlag} alt="العربية" className="flag-image"/>
                        </button>
                    </div>

                    {/* ── Modules Grid Dropdown ── */}
                    {showModules && (
                        <div className="dropdown-container" ref={modulesRef}>
                            <button className="icon-button modules-button"
                                    onClick={() => setIsModulesOpen(!isModulesOpen)} aria-label="Modules">
                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                                    <rect x="3" y="3" width="7" height="7" strokeWidth="2" strokeLinecap="round"
                                          strokeLinejoin="round"/>
                                    <rect x="14" y="3" width="7" height="7" strokeWidth="2" strokeLinecap="round"
                                          strokeLinejoin="round"/>
                                    <rect x="14" y="14" width="7" height="7" strokeWidth="2" strokeLinecap="round"
                                          strokeLinejoin="round"/>
                                    <rect x="3" y="14" width="7" height="7" strokeWidth="2" strokeLinecap="round"
                                          strokeLinejoin="round"/>
                                </svg>
                            </button>
                            {isModulesOpen && (
                                <div className="dropdown-menu modules-menu">
                                    <div className="dropdown-header">
                                        <h3>{t('Modules')}</h3>
                                    </div>
                                    <div className="modules-grid">
                                        {userModulesForSwitcher.length > 0 ? (
                                            userModulesForSwitcher.map((mod) => {
                                                const meta = MODULE_ICONS[mod.code] || {emoji: '📦', bg: '#EDF2FA'};
                                                return (
                                                    <div
                                                        key={mod.id}
                                                        className="module-card"
                                                        style={{'--module-bg': meta.bg}}
                                                        onClick={() => handleModuleSwitcherClick(mod)}
                                                    >
                                                        <div className="module-card-content">
                                                            <div className="module-card-icon"
                                                                 style={{backgroundColor: meta.bg}}>
                                                                <span style={{fontSize: 18}}>{meta.emoji}</span>
                                                            </div>
                                                            <div className="module-card-info">
                                                                <h4 className="module-card-title">{t(mod.name)}</h4>
                                                                <p className="module-card-description">{translateRole(mod.roleName)}</p>
                                                            </div>
                                                        </div>
                                                    </div>
                                                );
                                            })
                                        ) : (
                                            <div style={{ padding: '16px', color: '#6b7a99', fontSize: 12, textAlign: 'center', gridColumn: '1/-1' }}>
                                                {t('Aucun module disponible')}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    {/* ── Tasks ── */}
                    <div className="dropdown-container" ref={tasksRef}>
                        <button className="icon-button notification-button"
                                onClick={() => setIsTasksOpen(!isTasksOpen)}
                                aria-label={t('Tâches')}>
                            <CheckSquare size={22} />
                            {tasks.filter(task => task.status !== 'COMPLETED').length > 0 && (
                                <span className="notification-badge task-badge" style={{ backgroundColor: '#f59e0b' }}>
                                    {tasks.filter(task => task.status !== 'COMPLETED').length}
                                </span>
                            )}
                        </button>
                        {isTasksOpen && (
                            <div className="dropdown-menu notifications-menu">
                                <div className="dropdown-header">
                                    <h3>{t('Tâches à traiter')}</h3>
                                </div>
                                <div className="notifications-list">
                                    {tasks.length > 0 ? (
                                        tasks.map((task) => (
                                            <div 
                                                key={task.id} 
                                                className="notification-item unread"
                                            >
                                                <div className="notification-content" onClick={() => {
                                                    setIsTasksOpen(false);
                                                    if (task.routePath) navigate(task.routePath);
                                                }}>
                                                    <span className="unread-dot" style={{ backgroundColor: task.status === 'todo' ? '#f59e0b' : '#3b82f6' }}></span>
                                                    <div className="notification-text">
                                                        <p><strong>{task.title}</strong></p>
                                                        <span className="notification-time" style={{ color: '#6b7280', fontWeight: 'bold' }}>{task.moduleName || task.module}</span>
                                                    </div>
                                                </div>
                                            </div>
                                        ))
                                    ) : (
                                        <div className="no-notifications">
                                            <p className="no-notif-subtitle">{t('Aucune nouvelle tâche associée')}</p>
                                        </div>
                                    )}
                                </div>
                                {/*<div className="dropdown-footer" style={{ padding: '10px', textAlign: 'center', borderTop: '1px solid #e5e7eb' }}>*/}
                                {/*    <button onClick={() => navigate('/tasks')} style={{ background: 'none', border: 'none', color: '#1e3a8a', cursor: 'pointer', fontWeight: 600, fontSize: '0.85rem' }}>*/}
                                {/*        {t('Voir toutes mes tâches')}*/}
                                {/*    </button>*/}
                                {/*</div>*/}
                            </div>
                        )}
                    </div>

                    {/* ── Notifications ── */}
                    <div className="dropdown-container" ref={notificationsRef}>
                        <button className="icon-button notification-button"
                                onClick={() => setIsNotificationsOpen(!isNotificationsOpen)}
                                aria-label={t('Notifications')}>
                            <Bell size={22} />
                            {unreadCount > 0 && (
                                <span className="notification-badge">{unreadCountBadge}</span>
                            )}
                        </button>
                        {isNotificationsOpen && (
                            <div className="dropdown-menu notifications-menu">
                                <div className="dropdown-header">
                                    <h3>{t('Notifications')}</h3>
                                    <button 
                                        className={`mark-all-read ${unreadCount === 0 ? 'disabled' : ''}`} 
                                        onClick={() => unreadCount > 0 && dispatch(markAllNotificationsAsReadRequest(currentUser.id))}
                                        disabled={unreadCount === 0}
                                    >
                                        {t('Tout marquer comme lu')}
                                    </button>
                                </div>
                                <div className="notifications-list">
                                    {unreadNotifications.length > 0 ? (
                                        [...unreadNotifications]
                                            .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
                                            .map((notif) => (
                                            <div 
                                                key={notif.id} 
                                                className={`notification-item ${!notif.read ? 'unread' : ''}`}
                                            >
                                                <div className="notification-content" onClick={() => {
                                                    if (!notif.read) dispatch(markNotificationAsReadRequest(notif.id));
                                                    if (notif.link) navigate(notif.link);
                                                    setIsNotificationsOpen(false);
                                                }}>
                                                    {!notif.read && <span className="unread-dot"></span>}
                                                    <div className="notification-text">
                                                        <p><strong>{notif.title}</strong>: {notif.message}</p>
                                                        <span className="notification-time">{new Date(notif.createdAt).toLocaleDateString()}</span>
                                                    </div>
                                                </div>
                                                {!notif.read && (
                                                    <button 
                                                        className="mark-read-button" 
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            dispatch(markNotificationAsReadRequest(notif.id));
                                                        }}
                                                        title={t('Marquer comme lu')}
                                                    >
                                                        <Check size={16} />
                                                    </button>
                                                )}
                                            </div>
                                        ))
                                    ) : (
                                        <div className="no-notifications">
                                            {/*<div className="no-notif-icon">*/}
                                            {/*    <Bell size={28} strokeWidth={1.5}/>*/}
                                            {/*    <span className="no-notif-sparkle">✦</span>*/}
                                            {/*</div>*/}
                                            {/*<p className="no-notif-title">{t('Tout est à jour !')}</p>*/}
                                            <p className="no-notif-subtitle">{t('Aucune nouvelle notification pour le moment')}</p>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>

                    {/* ── User Profile ── */}
                    <div className="dropdown-container-roles" ref={userDropdownRef}>
                        <div className="user-profile" onClick={() => setIsUserDropdownOpen(!isUserDropdownOpen)}>
                            <div className="user-avatar">
                                {hasProfilePhoto ? (
                                    <img src={profilePhotoSrc} alt="Profile" className="profile-image"/>
                                ) : (
                                    <div className="user-avatar-initials" aria-label={`Avatar ${userInitials}`}>
                                        {userInitials}
                                    </div>
                                )}
                                <span className="status-indicator"></span>
                            </div>
                            <div className="user-info">
                                <span className="user-name">{currentUser?.fullName || t('Utilisateur')}</span>
                                {selectedRole && (
                                    <span className="current-role-badge">{translateRole(selectedRole)}</span>
                                )}
                            </div>
                        </div>

                        {isUserDropdownOpen && (
                            <div className="user-dropdown">
                                {userRoles.length > 0 && (
                                    <div className="user-dropdown-section">
                                        <h4 className="user-dropdown-section-title">{t('Rôle')} :</h4>
                                        <ul className="role-list">
                                            {userRoles.map((role) => (
                                                <li key={role.id}
                                                    className={`role-item ${selectedRole?.id === role.id ? 'active' : ''}`}
                                                    onClick={() => handleRoleChange(role)}>
                                                    <div className="role-icon">{getRoleIcon(role.name)}</div>
                                                    <div className="role-item-text">
                                                        <p className="role-name">{translateRole(role)}</p>
                                                    </div>
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                )}
                                <div className="user-dropdown-section">
                                    <div className="dropdown-menu-item" onClick={handleProfileClick}>
                                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
                                             stroke="currentColor">
                                            <path d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
                                                  strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                            <circle cx="12" cy="12" r="3" strokeWidth="2" strokeLinecap="round"
                                                    strokeLinejoin="round"/>
                                        </svg>
                                        <span>{t('Mon profil')}</span>
                                    </div>
                                    <div className="dropdown-menu-item logout" onClick={handleLogout}>
                                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
                                             stroke="currentColor">
                                            <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"
                                                  strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                            <polyline points="16 17 21 12 16 7" strokeWidth="2"
                                                      strokeLinecap="round" strokeLinejoin="round"/>
                                            <line x1="21" y1="12" x2="9" y2="12" strokeWidth="2"
                                                  strokeLinecap="round" strokeLinejoin="round"/>
                                        </svg>
                                        <span>{t('Se déconnecter')}</span>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>

                </div>
            </div>
        </div>
    );
};

export default UpperBar;
